import { PartnerMediaThumbnail } from '@/components/checklist/PartnerMediaThumbnail';
import { isStorageMediaReference } from '@/utils/media/partnerStorageReference';
import React, { useEffect, useMemo, useState } from 'react';
import {
  FlatList,
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  TextInput,
  TouchableOpacity,
  Image,
} from 'react-native';
import theme from '@/config/theme';
import { AdminLogger } from '@/utils/logger';
import { databaseService } from '@/services/databaseService';
import APP_CONSTANTS from '@/config/constants';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';

interface ChecklistItem {
  id: string;
  data_criacao: number;
  status: string;
  templateName: string;
  userName: string;
  sequencial_codigo?: string | null;
  sequencial_numero?: number | null;
}

interface Props {
  data: ChecklistItem[];
  loading: boolean;
  onEndReached: () => void;
  selectedIds: Set<string>;
  toggleSelect: (id: string) => void;
  hasMore?: boolean;
  openRows?: string[];
  onOpenRowsChange?: (rows: string[]) => void;
  refreshToken?: number;
}

// Tipos de campo que possuem imagem
const IMAGE_FIELD_TYPES = ['foto', 'assinatura', 'marcação', 'photo', 'signature', 'mark'];

// Helper para gerar thumbnail (80px de largura)
const makeThumb = (url: string) => isStorageMediaReference(url) ? url : `${url}${url.includes('?') ? '&' : '?'}width=80`;

const getLocalImageTableName = (value: string) => {
  if (value.startsWith('checklist-photos/')) {
    return APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_PHOTOS.NAME;
  }
  if (value.startsWith('checklist-signatures/')) {
    return APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_SIGNATURES.NAME;
  }
  if (value.startsWith('checklist-marcacoes/')) {
    return APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_MARCACOES.NAME;
  }
  return null;
};

const resolveLocalImageDataUrl = (base64: string, value: string) => {
  const isPng = value.toLowerCase().endsWith('.png');
  const mimeType = isPng ? 'image/png' : 'image/jpeg';
  return `data:${mimeType};base64,${base64}`;
};

// Estrutura detalhada de um valor
type DetailItem = {
  campo: string;
  valor: string;
  tipo: string;
  thumb?: string;
  grupo?: string;
  ordem?: number;
};

const getSequentialLabel = (item: ChecklistItem): string => {
  if (item.sequencial_codigo && item.sequencial_codigo.trim().length > 0) {
    return item.sequencial_codigo.trim();
  }
  if (typeof item.sequencial_numero === 'number' && Number.isFinite(item.sequencial_numero)) {
    return `Checklist #${item.sequencial_numero}`;
  }
  return 'Aguardando numeração';
};

export default function ChecklistsListNative({
  data,
  loading,
  onEndReached,
  selectedIds,
  toggleSelect,
  hasMore = true,
  openRows: openRowsProp,
  onOpenRowsChange,
  refreshToken,
}: Props) {
  const log = AdminLogger.List;
  const router = useRouter();
  log.info('📄 Render ChecklistsListNative', { count: data.length, loading });

  const [search, setSearch] = useState('');
  const [openRowsState, setOpenRowsState] = useState<string[]>([]);
  const [detailsCache, setDetailsCache] = useState<Record<string, DetailItem[]>>({});

  /**
   * Guarda o tamanho da lista para o qual já executamos `onEndReached`. Assim evitamos
   * loops quando o FlatList renderiza novamente mas nenhum item novo foi adicionado
   * (por exemplo, quando a busca está ativa ou o back-end já retornou tudo que havia).
   */
  const endReachedOnceFor = React.useRef<number>(-1);

  /**
   * Busca detalhes do checklist.
   * @param rowId ID do checklist
   * @param includeThumbs Se true, gera thumbnails para campos de imagem
   */
  const fetchDetails = async (
    rowId: string,
    includeThumbs: boolean,
    options?: { force?: boolean }
  ) => {
    if (detailsCache[rowId] && !options?.force) {
      // Caso já exista no cache mas precise gerar thumbs ausentes
      if (includeThumbs) {
        const needs = detailsCache[rowId].some((d) => IMAGE_FIELD_TYPES.includes(d.tipo) && !d.thumb);
        if (!needs) return;

        const updated = detailsCache[rowId].map((d) => {
          if (IMAGE_FIELD_TYPES.includes(d.tipo) && !d.thumb) {
            const t = makeThumb(d.valor);
            log.info('Thumb gerado (cache)', { campo: d.campo, url: t });
            return { ...d, thumb: t };
          }
          return d;
        });
        setDetailsCache((prev) => ({ ...prev, [rowId]: updated }));
      }
      return;
    }
    try {
      const valores = await databaseService.listItems<any>(
        APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME,
        { checklist_id: rowId },
      );
      const detalhados: DetailItem[] = [];
      for (const val of valores) {
        if (!val.valor) continue;
        let campoNome = '';
        let campoTipo = '';
        let campoGrupo = '';
        let campoOrdem = 0;
        if (val.campo_id) {
          const campo = await databaseService.readItem<any>(
            APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME,
            val.campo_id,
          );
          campoNome = campo?.nome || '';
          campoTipo = campo?.tipo || '';
          campoGrupo = campo?.grupo || '';
          campoOrdem = campo?.ordem ?? 0;
        }

        let thumb: string | undefined;
        if (includeThumbs && IMAGE_FIELD_TYPES.includes(campoTipo)) {
          const valueStr = String(val.valor);
          const localTable = getLocalImageTableName(valueStr);
          if (localTable) {
            try {
              const row = await databaseService.readItem<{ base64?: string }>(localTable, valueStr);
              if (row?.base64) {
                thumb = resolveLocalImageDataUrl(row.base64, valueStr);
              }
            } catch (error) {
            }
          } else {
            thumb = makeThumb(valueStr);
            log.info('🔗 Consulta de rede para thumb', { campo: campoNome, valor: valueStr, url: thumb });
          }
        }

        detalhados.push({ campo: campoNome, valor: String(val.valor), tipo: campoTipo, grupo: campoGrupo, ordem: campoOrdem, thumb });
      }
      setDetailsCache((prev) => {
        if (!includeThumbs && prev[rowId]) {
          return prev;
        }
        return { ...prev, [rowId]: detalhados };
      });
    } catch (err) {
      log.error('Erro carregando detalhes checklist', rowId, err);
    }
  };

  const openRows = openRowsProp ?? openRowsState;
  const updateOpenRows = (rows: string[]) => {
    if (onOpenRowsChange) {
      onOpenRowsChange(rows);
      return;
    }
    setOpenRowsState(rows);
  };

  const toggleRow = async (rowId: string) => {
    if (openRows.includes(rowId)) {
      updateOpenRows(openRows.filter((id) => id !== rowId));
      return;
    }
    updateOpenRows([...openRows, rowId]);
    await fetchDetails(rowId, true);
  };

  // Prefetch detalhes sempre que lista de dados mudar
  useEffect(() => {
    data.forEach((row) => {
      if (!detailsCache[row.id]) {
        fetchDetails(row.id, false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (!openRows.length) {
      return;
    }
    openRows.forEach((rowId) => {
      fetchDetails(rowId, true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRows]);

  useEffect(() => {
    if (refreshToken === undefined) {
      return;
    }
    setDetailsCache({});
    if (!openRows.length) {
      return;
    }
    setTimeout(() => {
      openRows.forEach((rowId) => {
        fetchDetails(rowId, true, { force: true });
      });
    }, 0);
  }, [refreshToken]);

  const filteredData = useMemo(() => {
    if (!search) return data;
    const term = search.toLowerCase();
    return data.filter((r) => {
      const sequential = getSequentialLabel(r).toLowerCase();
      const basicMatch =
        r.templateName.toLowerCase().includes(term) ||
        r.userName.toLowerCase().includes(term) ||
        statusLabel(r.status).toLowerCase().includes(term) ||
        sequential.includes(term);
      if (basicMatch) return true;
      const details = detailsCache[r.id];
      if (details) {
        return details.some((d) => d.valor.toLowerCase().includes(term) || d.campo.toLowerCase().includes(term));
      }
      return false;
    });
  }, [search, data, detailsCache]);

  // ===== Highlight do termo pesquisado =====
  const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
  const highlightTerm = (text: string): React.ReactNode => {
    if (!search) return text;
    const regex = new RegExp(`(${terms.join('|')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <Text key={i} style={styles.highlight}>{part}</Text>
      ) : (
        part
      ),
    );
  };

  const renderItem = ({ item, index }: { item: ChecklistItem; index: number }) => {
    const isOpen = openRows.includes(item.id);
    const hasSequentialValue =
      (item.sequencial_codigo && item.sequencial_codigo.trim().length > 0) ||
      (typeof item.sequencial_numero === 'number' && Number.isFinite(item.sequencial_numero));
    return (
      <>
        <TouchableOpacity
          style={[styles.row, { backgroundColor: getRowBackground(index) }]}
          onPress={() => {
            if (selectedIds.size > 0) {
              toggleSelect(item.id);
            } else {
              toggleRow(item.id);
            }
          }}
        >
          {/* Checkbox */}
          <TouchableOpacity
            onPress={() => {
              toggleSelect(item.id);
            }}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: selectedIds.has(item.id) }}
            style={{ marginRight: 4 }}
          >
            <MaterialCommunityIcons
              name={selectedIds.has(item.id) ? 'checkbox-marked' : 'checkbox-blank-outline'}
              size={20}
              color={theme.colors.primary.default}
            />
          </TouchableOpacity>

          {/* Chevron */}
          <MaterialCommunityIcons
            name="chevron-right"
            size={22}
            color={openRows.includes(item.id) ? theme.colors.primary.default : theme.colors.text.secondary}
            style={{
              marginRight: 8,
              transform: [{ rotate: openRows.includes(item.id) ? '90deg' : '0deg' }],
            }}
          />

          {/* Data */}
          <Text style={styles.dateText}>{highlightTerm(formatDate(item.data_criacao))}</Text>

          {/* Template + Usuário */}
          <View style={styles.detailCol}>
            <Text style={[styles.sequenceText, !hasSequentialValue && styles.sequenceTextPending]}>
              {highlightTerm(`Número: ${getSequentialLabel(item)}`)}
            </Text>
            <Text style={styles.templateText}>{highlightTerm(item.templateName)}</Text>
            <Text style={styles.userText}>{highlightTerm(item.userName)}</Text>
          </View>

          {/* Status */}
          <View style={[styles.chip, { backgroundColor: statusColor(item.status) }]}> 
            <Text style={styles.chipText}>{highlightTerm(statusLabel(item.status))}</Text>
          </View>
        </TouchableOpacity>
        {isOpen && (
          <View style={styles.detailsContainer}>
            <TouchableOpacity
              style={styles.editButton}
              onPress={() => router.push({
                pathname: '/(app)/checklist/[id]',
                params: { id: item.id, returnTo: '/(app)/admin/dashboard' },
              })}
              accessibilityRole="button"
              accessibilityLabel={`Editar checklist ${item.id}`}
              disabled={selectedIds.size > 0}
            >
              <MaterialCommunityIcons
                name="pencil-outline"
                size={18}
                color={selectedIds.size > 0 ? theme.colors.text.tertiary : theme.colors.primary.default}
              />
              <Text style={[styles.editButtonText, selectedIds.size > 0 && styles.editButtonTextDisabled]}>
                Editar checklist
              </Text>
            </TouchableOpacity>
            {detailsCache[item.id] ? (
              detailsCache[item.id].length ? (
                (() => {
                  const details = detailsCache[item.id];
                  // Agrupa por grupo
                  const groups: Record<string, DetailItem[]> = {};
                  details.forEach((d) => {
                    const g = d.grupo || '';
                    if (!groups[g]) groups[g] = [];
                    groups[g].push(d);
                  });
                  const orderedGroups = Object.entries(groups).sort((a, b) => {
                    const orderA = a[1][0]?.ordem ?? 0;
                    const orderB = b[1][0]?.ordem ?? 0;
                    return orderA - orderB;
                  });
                  return orderedGroups.map(([groupName, items]) => (
                    <View key={groupName} style={{ marginBottom: 8 }}>
                      {groupName ? (
                        <Text style={{ fontWeight: '600', marginBottom: 4, color: theme.colors.text.primary }}>
                          {groupName}
                        </Text>
                      ) : null}
                      {items.sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)).map((d, idx) => (
                        <View key={idx} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
                          {d.tipo === 'mensagem pronta' ? (
                            <Text style={[styles.detailText, { fontWeight: 'bold' }]}>{highlightTerm(d.valor)}</Text>
                          ) : (
                            <>
                              {d.campo ? (
                                <Text style={[styles.detailText, { fontWeight: 'bold' }]}>{highlightTerm(d.campo)}: </Text>
                              ) : null}
                              {IMAGE_FIELD_TYPES.includes(d.tipo) && isStorageMediaReference(d.valor) ? (
                                  <PartnerMediaThumbnail value={d.valor} checklistId={item.id} />
                                ) : IMAGE_FIELD_TYPES.includes(d.tipo) && d.thumb ? (
                                <Image
                                  source={{ uri: d.thumb }}
                                  style={{ width: 60, height: 60, marginRight: 6, borderRadius: 4 }}
                                  resizeMode="cover"
                                />
                              ) : null}
                              {IMAGE_FIELD_TYPES.includes(d.tipo) ? null : (
                                <Text style={styles.detailText}>{highlightTerm(d.valor)}</Text>
                              )}
                            </>
                          )}
                        </View>
                      ))}
                    </View>
                  ));
                })()
              ) : (
                <Text style={styles.detailTextEmpty}>Nenhum valor de texto.</Text>
              )
            ) : (
              <ActivityIndicator />
            )}
          </View>
        )}
      </>
    );
  };

  return (
    <View style={{ flex: 1 }}>
      {/* Barra de pesquisa */}
      <View style={{ paddingHorizontal: 16, paddingVertical: 8 }}>
        <TextInput
          placeholder="Pesquisar..."
          value={search}
          onChangeText={setSearch}
          style={styles.searchInput}
          placeholderTextColor={theme.colors.text.secondary}
        />
      </View>
      <FlatList
        data={filteredData}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        // Evita loop infinito: só chama quando (1) não está filtrando e (2) ainda não chamamos para esse tamanho de lista
        onEndReached={() => {
          if (!hasMore) return; // Evita chamadas desnecessárias quando não há mais dados
          log.info('🔚 onEndReached');
          const isFiltering = search.trim().length > 0;

          if (isFiltering) {
            log.info('🔍 Ignorando onEndReached por estar filtrando');
            return;
          }

          if (!loading && data.length > 0 && data.length !== endReachedOnceFor.current) {
            endReachedOnceFor.current = data.length;
            onEndReached();
          } else {
            log.info(
              '⏭️ Ignorando onEndReached: loading=%s, data.length=%d, já processado? %s',
              loading,
              data.length,
              data.length === endReachedOnceFor.current,
            );
          }
        }}
        onEndReachedThreshold={0.2}
        ListFooterComponent={loading ? <ActivityIndicator style={{ margin: 16 }} /> : null}
      />
    </View>
  );
}

// ===== Utilitários =====

const statusLabel = (s: string) => {
  switch (s) {
    case 'in_progress':
      return 'Em andamento';
    case 'done':
      return 'Concluído';
    case 'pending':
      return 'Pendente';
    default:
      return s;
  }
};

const statusColor = (s: string) => {
  switch (s) {
    case 'in_progress':
      return theme.colors.secondary.default;
    case 'done':
      return theme.colors.util.success;
    case 'pending':
      return theme.colors.util.warning;
    default:
      return theme.colors.border.dark;
  }
};

const getRowBackground = (index: number) =>
  index % 2 === 0 ? theme.colors.background.primary : theme.colors.background.tertiary;

const formatDate = (ts: number) => new Date(ts).toLocaleDateString('pt-BR');

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 8,
  },
  cell: {
    color: theme.colors.text.primary,
    marginRight: 8,
  },
  chip: {
    paddingHorizontal: theme.spacing.xs,
    paddingVertical: 2,
    borderRadius: theme.borderRadius.md,
  },
  chipText: {
    color: theme.colors.white,
    fontSize: theme.fontSizes.xs,
    fontWeight: '600',
    textAlign: 'center',
  },
  detailsContainer: {
    paddingHorizontal: 24,
    paddingVertical: 8,
    backgroundColor: theme.colors.background.tertiary,
  },
  editButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    backgroundColor: theme.colors.background.primary,
    marginBottom: 8,
  },
  editButtonText: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.sm,
    fontWeight: '600',
  },
  editButtonTextDisabled: {
    color: theme.colors.text.tertiary,
  },
  detailText: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.sm,
  },
  detailTextEmpty: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
  },
  searchInput: {
    width: '100%',
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    borderRadius: theme.borderRadius.md,
    paddingHorizontal: 12,
    paddingVertical: 8,
    color: theme.colors.text.primary,
  },
  highlight: {
    backgroundColor: theme.colors.util.warning,
    borderRadius: 4,
    paddingHorizontal: 2,
  },
  dateText: {
    color: theme.colors.text.primary,
    width: 90,
    marginRight: 8,
  },
  detailCol: {
    flex: 1,
    marginRight: 8,
  },
  templateText: {
    color: theme.colors.text.primary,
    fontWeight: '600',
  },
  userText: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
  },
  sequenceText: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.sm,
    fontWeight: '600',
  },
  sequenceTextPending: {
    color: theme.colors.text.secondary,
    fontStyle: 'italic',
  },
});
